import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';
import RelatedContent from '@/components/RelatedContent';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Play, Search, Filter, Clock, Eye, ThumbsUp, Calendar, User, Star, TrendingUp, Award } from 'lucide-react';

const VideosPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const videos = [
  {
    id: 1,
    title: "Cine en Casa Automatizado - Proyecto Completo",
    description: "Descubre cómo crear la experiencia de cine perfecta con automatización completa de audio, video, luces y cortinas. En este video te mostramos paso a paso la implementación de un sistema completo de cine en casa con HomeSeer.",
    youtubeId: "MbOWj9cp0WI",
    thumbnail: "https://img.youtube.com/vi/MbOWj9cp0WI/maxresdefault.jpg",
    duration: "12:45",
    views: 15400,
    likes: 890,
    category: "Proyectos",
    date: "2024-01-15",
    featured: true
  },
  {
    id: 2,
    title: "Fibaro Swipe - Control Gestual Revolucionario",
    description: "Conoce el Fibaro Swipe y cómo revoluciona el control domótico con gestos intuitivos y elegantes. Una nueva forma de interactuar con tu hogar inteligente sin necesidad de tocar ningún dispositivo.",
    youtubeId: "txX0mA5L7g4",
    thumbnail: "https://img.youtube.com/vi/txX0mA5L7g4/maxresdefault.jpg",
    duration: "8:30",
    views: 9200,
    likes: 567,
    category: "Productos",
    date: "2024-01-10",
    featured: false
  },
  {
    id: 3,
    title: "HomeSeer vs Competencia - Comparativa Completa",
    description: "Análisis detallado de por qué HomeSeer supera a la competencia en funcionalidad, precio y facilidad de uso. Comparamos características, precios y capacidades de los principales sistemas domóticos del mercado.",
    youtubeId: "3cM5qblhdxU",
    thumbnail: "https://img.youtube.com/vi/3cM5qblhdxU/maxresdefault.jpg",
    duration: "15:20",
    views: 22100,
    likes: 1340,
    category: "Comparativas",
    date: "2024-01-05",
    featured: false
  },
  {
    id: 4,
    title: "Automatización de Iluminación Inteligente",
    description: "Guía completa para automatizar la iluminación de tu hogar con sensores, dimmers y programación avanzada. Aprende a crear ambientes perfectos para cada momento del día.",
    youtubeId: "demo4",
    thumbnail: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=400&fit=crop&crop=center",
    duration: "10:15",
    views: 7800,
    likes: 456,
    category: "Tutoriales",
    date: "2024-01-03",
    featured: false
  },
  {
    id: 5,
    title: "Seguridad Domótica: Cámaras y Sensores",
    description: "Implementa un sistema de seguridad completo con cámaras IP, sensores de movimiento y alertas inteligentes. Protege tu hogar con la tecnología más avanzada.",
    youtubeId: "demo5",
    thumbnail: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&h=400&fit=crop&crop=center",
    duration: "14:30",
    views: 12600,
    likes: 789,
    category: "Seguridad",
    date: "2024-01-01",
    featured: false
  },
  {
    id: 6,
    title: "Control de Climatización Inteligente",
    description: "Optimiza el consumo energético con termostatos inteligentes y zonificación automatizada. Mantén la temperatura perfecta mientras ahorras en tu factura eléctrica.",
    youtubeId: "demo6",
    thumbnail: "https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=600&h=400&fit=crop&crop=center",
    duration: "11:45",
    views: 9900,
    likes: 623,
    category: "Climatización",
    date: "2023-12-28",
    featured: false
  },
  {
    id: 7,
    title: "Jardín Inteligente: Riego Automatizado",
    description: "Crea un sistema de riego inteligente que cuide tu jardín automáticamente. Sensores de humedad, pronóstico del tiempo y programación avanzada.",
    youtubeId: "demo7",
    thumbnail: "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=600&h=400&fit=crop&crop=center",
    duration: "9:20",
    views: 6800,
    likes: 412,
    category: "Jardín",
    date: "2023-12-25",
    featured: false
  },
  {
    id: 8,
    title: "Automatización de Oficina en Casa",
    description: "Transforma tu oficina en casa con automatización inteligente. Iluminación adaptativa, control de ruido y ambientes productivos.",
    youtubeId: "demo8",
    thumbnail: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=600&h=400&fit=crop&crop=center",
    duration: "13:15",
    views: 8900,
    likes: 534,
    category: "Oficina",
    date: "2023-12-20",
    featured: false
  }];


  const categories = ["Todas", "Proyectos", "Productos", "Comparativas", "Tutoriales", "Seguridad", "Climatización", "Jardín", "Oficina"];

  const filteredVideos = videos.filter((video) => {
    const matchesSearch = video.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    video.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || selectedCategory === "Todas" || video.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredVideo = videos.find((v) => v.featured) || videos[0];

  const VideoCard = ({ video, isLarge = false }: {video: any;isLarge?: boolean;}) =>
  <Card className={`overflow-hidden hover:shadow-lg transition-all duration-300 ${isLarge ? 'md:col-span-2' : ''}`} data-id="lx4ffviu8" data-path="src/pages/VideosPage.tsx">
      <div className="relative group cursor-pointer" data-id="vr9k7viiu" data-path="src/pages/VideosPage.tsx">
        <div className={`aspect-video overflow-hidden ${isLarge ? 'h-64 md:h-80' : 'h-48'}`} data-id="agkrovgn6" data-path="src/pages/VideosPage.tsx">
          <img
          src={video.thumbnail}
          alt={video.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" data-id="n6zi4v125" data-path="src/pages/VideosPage.tsx" />
          <div className="absolute inset-0 bg-black bg-opacity-30 group-hover:bg-opacity-20 transition-opacity duration-300" data-id="w29etp5m9" data-path="src/pages/VideosPage.tsx"></div>
          <div className="absolute inset-0 flex items-center justify-center" data-id="iff5al0jc" data-path="src/pages/VideosPage.tsx">
            <div className="bg-red-600 hover:bg-red-700 transition-colors duration-300 rounded-full p-4 transform group-hover:scale-110" data-id="bph5q3w27" data-path="src/pages/VideosPage.tsx">
              <Play className="w-8 h-8 text-white ml-1" data-id="6q8v3iszb" data-path="src/pages/VideosPage.tsx" />
            </div>
          </div>
          <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded" data-id="p32ttf6yv" data-path="src/pages/VideosPage.tsx">
            {video.duration}
          </div>
        </div>
      </div>
      <CardContent className={`p-${isLarge ? '8' : '6'}`} data-id="0ney59n7i" data-path="src/pages/VideosPage.tsx">
        <div className="flex items-center space-x-4 mb-3" data-id="mlnfr5p4w" data-path="src/pages/VideosPage.tsx">
          <Badge variant="secondary" className="bg-red-100 text-red-800" data-id="a7em8di6r" data-path="src/pages/VideosPage.tsx">
            {video.category}
          </Badge>
          <div className="flex items-center space-x-2 text-gray-500 text-sm" data-id="yn9mznn8z" data-path="src/pages/VideosPage.tsx">
            <Calendar className="w-4 h-4" data-id="s539ctfym" data-path="src/pages/VideosPage.tsx" />
            <span data-id="kizgbyryt" data-path="src/pages/VideosPage.tsx">{new Date(video.date).toLocaleDateString('es-ES')}</span>
          </div>
        </div>
        <h3 className={`font-bold text-gray-800 mb-3 ${isLarge ? 'text-2xl' : 'text-lg'} line-clamp-2`} data-id="m7ctztrjc" data-path="src/pages/VideosPage.tsx">
          {video.title}
        </h3>
        <p className={`text-gray-600 mb-4 ${isLarge ? 'text-base' : 'text-sm'} line-clamp-3`} data-id="vk9goxe1a" data-path="src/pages/VideosPage.tsx">
          {video.description}
        </p>
        <div className="flex items-center justify-between" data-id="gj6rt5b79" data-path="src/pages/VideosPage.tsx">
          <div className="flex items-center space-x-4 text-sm text-gray-500" data-id="xjcjifjv4" data-path="src/pages/VideosPage.tsx">
            <div className="flex items-center space-x-1" data-id="s0rsmfokp" data-path="src/pages/VideosPage.tsx">
              <Eye className="w-4 h-4" data-id="mzpollwdq" data-path="src/pages/VideosPage.tsx" />
              <span data-id="9lgwlvmhf" data-path="src/pages/VideosPage.tsx">{video.views.toLocaleString()}</span>
            </div>
            <div className="flex items-center space-x-1" data-id="teql8b2fb" data-path="src/pages/VideosPage.tsx">
              <ThumbsUp className="w-4 h-4" data-id="m75o5owf6" data-path="src/pages/VideosPage.tsx" />
              <span data-id="0oyjxvaof" data-path="src/pages/VideosPage.tsx">{video.likes.toLocaleString()}</span>
            </div>
          </div>
          <Button
          variant="outline"
          size="sm"
          className="hover:bg-red-50 hover:border-red-300"
          onClick={() => window.open(`https://www.youtube.com/watch?v=${video.youtubeId}`, '_blank')} data-id="f3yrx42lk" data-path="src/pages/VideosPage.tsx">
            Ver Video
          </Button>
        </div>
      </CardContent>
    </Card>;


  return (
    <div className="min-h-screen bg-gray-50" data-id="yzpb283qc" data-path="src/pages/VideosPage.tsx">
      <Header data-id="bdga9dvdh" data-path="src/pages/VideosPage.tsx" />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-red-600 to-purple-600 text-white py-20" data-id="xqy8du3ap" data-path="src/pages/VideosPage.tsx">
        <div className="container mx-auto px-4" data-id="hq4mbk6xy" data-path="src/pages/VideosPage.tsx">
          <div className="max-w-4xl mx-auto text-center" data-id="967o1m3tl" data-path="src/pages/VideosPage.tsx">
            <h1 className="text-4xl md:text-6xl font-bold mb-6" data-id="luto92axi" data-path="src/pages/VideosPage.tsx">
              Videos de Automatiza.me
            </h1>
            <p className="text-xl md:text-2xl text-red-100 mb-8" data-id="75vnqbxno" data-path="src/pages/VideosPage.tsx">
              Tutoriales, demos y proyectos reales de domótica y automatización
            </p>
            <div className="flex items-center justify-center space-x-4 text-red-100" data-id="7xi2u9jsm" data-path="src/pages/VideosPage.tsx">
              <div className="flex items-center space-x-2" data-id="9oa0ckhdq" data-path="src/pages/VideosPage.tsx">
                <Play className="w-5 h-5" data-id="ilj5o2ju7" data-path="src/pages/VideosPage.tsx" />
                <span data-id="pch12ubtb" data-path="src/pages/VideosPage.tsx">Demos en Vivo</span>
              </div>
              <div className="w-px h-6 bg-red-300" data-id="nvpt6xtye" data-path="src/pages/VideosPage.tsx"></div>
              <div className="flex items-center space-x-2" data-id="u1kfwxpep" data-path="src/pages/VideosPage.tsx">
                <User className="w-5 h-5" data-id="3v0zatho5" data-path="src/pages/VideosPage.tsx" />
                <span data-id="ifyqzx5k5" data-path="src/pages/VideosPage.tsx">Proyectos Reales</span>
              </div>
              <div className="w-px h-6 bg-red-300" data-id="63bsc4ptn" data-path="src/pages/VideosPage.tsx"></div>
              <div className="flex items-center space-x-2" data-id="wuixdsano" data-path="src/pages/VideosPage.tsx">
                <Award className="w-5 h-5" data-id="on2jxxrg4" data-path="src/pages/VideosPage.tsx" />
                <span data-id="z4jokn4r6" data-path="src/pages/VideosPage.tsx">Casos de Éxito</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-16" data-id="rwbmrdoyz" data-path="src/pages/VideosPage.tsx">
        <Breadcrumbs items={[{ label: 'Videos' }]} data-id="f8rtf723a" data-path="src/pages/VideosPage.tsx" />
        
        {/* Stats Section */}
        <div className="max-w-4xl mx-auto mb-16" data-id="zo41hpect" data-path="src/pages/VideosPage.tsx">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="d5d63b04l" data-path="src/pages/VideosPage.tsx">
            <Card className="text-center p-6" data-id="71m4121xf" data-path="src/pages/VideosPage.tsx">
              <div className="flex items-center justify-center mb-4" data-id="83xezjbic" data-path="src/pages/VideosPage.tsx">
                <div className="bg-red-100 p-3 rounded-full" data-id="1o0j4iror" data-path="src/pages/VideosPage.tsx">
                  <Play className="w-8 h-8 text-red-600" data-id="uk1t5pf1k" data-path="src/pages/VideosPage.tsx" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2" data-id="73ywj6tvq" data-path="src/pages/VideosPage.tsx">100+</h3>
              <p className="text-gray-600" data-id="x2nasxguz" data-path="src/pages/VideosPage.tsx">Videos Publicados</p>
            </Card>
            
            <Card className="text-center p-6" data-id="2q62gcpna" data-path="src/pages/VideosPage.tsx">
              <div className="flex items-center justify-center mb-4" data-id="0qk3f2to4" data-path="src/pages/VideosPage.tsx">
                <div className="bg-green-100 p-3 rounded-full" data-id="gzwj4frgv" data-path="src/pages/VideosPage.tsx">
                  <TrendingUp className="w-8 h-8 text-green-600" data-id="mjqq4bneu" data-path="src/pages/VideosPage.tsx" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2" data-id="p934bkln1" data-path="src/pages/VideosPage.tsx">500K+</h3>
              <p className="text-gray-600" data-id="nhim9a027" data-path="src/pages/VideosPage.tsx">Visualizaciones</p>
            </Card>
            
            <Card className="text-center p-6" data-id="4js0n2892" data-path="src/pages/VideosPage.tsx">
              <div className="flex items-center justify-center mb-4" data-id="ynvky222t" data-path="src/pages/VideosPage.tsx">
                <div className="bg-purple-100 p-3 rounded-full" data-id="s5hzxqhr8" data-path="src/pages/VideosPage.tsx">
                  <ThumbsUp className="w-8 h-8 text-purple-600" data-id="t2pg58a05" data-path="src/pages/VideosPage.tsx" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2" data-id="54daq3ldt" data-path="src/pages/VideosPage.tsx">15K+</h3>
              <p className="text-gray-600" data-id="053wg8pfq" data-path="src/pages/VideosPage.tsx">Suscriptores</p>
            </Card>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="max-w-4xl mx-auto mb-12" data-id="z382v39gf" data-path="src/pages/VideosPage.tsx">
          <div className="flex flex-col md:flex-row gap-4 mb-6" data-id="jozsg4jw3" data-path="src/pages/VideosPage.tsx">
            <div className="flex-1 relative" data-id="rkgb8hatj" data-path="src/pages/VideosPage.tsx">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" data-id="txt4sbvoz" data-path="src/pages/VideosPage.tsx" />
              <Input
                placeholder="Buscar videos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10" data-id="u6xwroghm" data-path="src/pages/VideosPage.tsx" />
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2" data-id="kmc97ohft" data-path="src/pages/VideosPage.tsx">
            {categories.map((category) =>
            <Button
              key={category}
              variant={selectedCategory === category ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(category)}
              className={selectedCategory === category ?
              "bg-red-600 hover:bg-red-700" :
              "hover:bg-red-50 hover:border-red-300"
              } data-id="a7jzvjd9n" data-path="src/pages/VideosPage.tsx">
                {category}
              </Button>
            )}
          </div>
        </div>

        {/* Featured Video */}
        <div className="max-w-6xl mx-auto mb-16" data-id="7ixlm99ni" data-path="src/pages/VideosPage.tsx">
          <div className="flex items-center justify-between mb-8" data-id="lf6n1hkml" data-path="src/pages/VideosPage.tsx">
            <h2 className="text-3xl font-bold text-gray-800" data-id="t8lsasawr" data-path="src/pages/VideosPage.tsx">Video Destacado</h2>
            <Badge variant="outline" className="border-yellow-500 text-yellow-700" data-id="tj0tjzy1e" data-path="src/pages/VideosPage.tsx">
              <Star className="w-4 h-4 mr-1" data-id="6y8geun4q" data-path="src/pages/VideosPage.tsx" />
              Destacado
            </Badge>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="vdhqgllfz" data-path="src/pages/VideosPage.tsx">
            <VideoCard video={featuredVideo} isLarge={true} data-id="dj4gcqgiw" data-path="src/pages/VideosPage.tsx" />
          </div>
        </div>

        {/* Videos Grid */}
        <div className="max-w-6xl mx-auto" data-id="fl2fail6e" data-path="src/pages/VideosPage.tsx">
          <div className="flex items-center justify-between mb-8" data-id="jon15541q" data-path="src/pages/VideosPage.tsx">
            <h2 className="text-3xl font-bold text-gray-800" data-id="1hpf39wn1" data-path="src/pages/VideosPage.tsx">Todos los Videos</h2>
            <div className="text-gray-500" data-id="04s83e1ai" data-path="src/pages/VideosPage.tsx">
              {filteredVideos.length} video{filteredVideos.length !== 1 ? 's' : ''} encontrado{filteredVideos.length !== 1 ? 's' : ''}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="6z7zgqi78" data-path="src/pages/VideosPage.tsx">
            {filteredVideos.filter((v) => !v.featured).map((video) =>
            <VideoCard key={video.id} video={video} data-id="h5uqe5l37" data-path="src/pages/VideosPage.tsx" />
            )}
          </div>
        </div>

        {/* CTA Section */}
        <div className="max-w-4xl mx-auto mt-16" data-id="19cfxol00" data-path="src/pages/VideosPage.tsx">
          <Card className="bg-gradient-to-r from-red-600 to-purple-600 text-white" data-id="l20xepe9b" data-path="src/pages/VideosPage.tsx">
            <CardContent className="p-8 text-center" data-id="viunxy88z" data-path="src/pages/VideosPage.tsx">
              <h3 className="text-2xl font-bold mb-4" data-id="ymizzkbes" data-path="src/pages/VideosPage.tsx">¿Tienes un proyecto en mente?</h3>
              <p className="text-red-100 mb-6" data-id="6bdbed9dd" data-path="src/pages/VideosPage.tsx">
                Contáctanos para una consulta gratuita y descubre cómo podemos automatizar tu hogar
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="0erq5pypi" data-path="src/pages/VideosPage.tsx">
                <Button
                  className="bg-white text-red-600 hover:bg-gray-100"
                  onClick={() => window.open('https://www.youtube.com/channel/UCYxCsoYcLTkWvUpgBLbWlTA', '_blank')} data-id="14ieg208x" data-path="src/pages/VideosPage.tsx">
                  Suscribirse al Canal
                </Button>
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-red-600" data-id="pnopixmeq" data-path="src/pages/VideosPage.tsx">
                  Consulta Gratuita
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer data-id="bskoljfo4" data-path="src/pages/VideosPage.tsx" />
    </div>);

};

export default VideosPage;